import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';

import { useIntl } from 'react-intl';
import messages from './messages';

import { getStyles } from './styles';
import { Box, Breadcrumbs, Button, IconButton, Link, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { IconSvg } from '../../../../utils/globalIcons';
import { v4 as uuid } from 'uuid';

import { ArrowBack, Cancel, NavigateNextRounded } from '@mui/icons-material';
import LoadBar from '../../../../utils/LoadBar';
import AnalyticsApi from '../../../../Api/Services/AmeyaBackend/AnalyticsToolApi';
import { useAppContext } from '../../../../context/AppContext';
import { tostAlert } from '../../../../utils/AlertToast';

import AmeyaAuthApi from '../../../../Api/Services/AppflyteAuth/AmeyaAuthService';
import getSubscriptionById from '../../../../utils/ApiFunctions/getSubscriptionDataById';
import { fetchSubscriptionId, fetchUserEmail, getUserName } from '../../../../utils/GetAccountDetails';

import GoogleDrive from './Datasource/google_drive';
import Files from './Datasource/files';
import Links from './Datasource/links';
import MongoDB from './Datasource/mongodb';
import OneDrive from './Datasource/one_drive';
// import AWS_S3 from './Datasource/aws_s3';
import Gmail from './Datasource/gmail';
import Outlook from './Datasource/outlook';
import AmeyaExtract from './Datasource/ameya_extract';
import MonitoringServiceApi from '../../../../Api/Services/AmeyaBackend/MonitoringServiceApi';
import { useDispatch, useSelector } from 'react-redux';
import AnalyticsFilesData from '../../../../utils/ApiFunctions/AnalyticsFilesData';
import moment from 'moment';
import { getAgentApiToken } from '../../../../utils';
import AnalyticsDataSetsApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSets';
import { setAnalyticsDataSetAdded, setAnalyticsDataSourcesAdded } from '../../../../Redux/slice/newDataSlice';
import Appflyte from './Datasource/appflyte';
import AnalyticsDataSourcesApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSources';

const INTERVAL_SECONDS = {
    one_minute: 60,
    five_minute: 300,
    ten_minute: 600,
    twenty_minute: 1200,
    thirty_minute: 1800,

    one_hour: 3600,
    two_hour: 7200,
    three_hour: 10800,
    four_hour: 14400,
    five_hour: 18000,
    six_hour: 21600,

    one_day: 86400,
    one_week: 604800,

    daily: 86400,
    weekly: 604800,
    monthly: 2592000,   // 30 days
    yearly: 31536000    // 365 days
};

const INITIAL_STATES = {
    googledrive: {
        googledriveAvailable: false,
        googledriveConnector: {},
        googledriveFolders: [],
        googledriveSelectedFolders: null,
        accessToken: null,
        refreshToken: null,
        loading: false,
        formErrors: { folderSelected: null, import_action: null },
        importAction: 'New file is created in source folder',
        importInterval: 'one_minute'
    },
    onedrive: {
        onedriveAvailable: false,
        onedriveConnector: {},
        onedriveFolders: [],
        onedriveSelectedFolders: null,
        accessToken: null,
        refreshToken: null,
        loading: false,
        formErrors: { folderSelected: null, import_action: null },
        importAction: 'New file is created in source folder',
        importInterval: 'one_minute'
    },
    gmail: {
        gmailAvailable: false,
        gmailConnector: {},
        accessToken: null,
        refreshToken: null,
        loading: false,
        subjectMessage: '',
        importInterval: 'one_minute'
    },
    outlook: {
        outlookAvailable: false,
        outlookConnector: {},
        accessToken: null,
        refreshToken: null,
        loading: false,
        subjectMessage: '',
        importInterval: 'one_minute'
    },
    links: {
        tempUrl: "",
        pendingUrls: [],
        links: [],
        loading: false,
        importInterval: 'one_hour',
        syncNow: false
    },
    files: {
        fileAvailable: false,
        fileList: [],
        loading: false,
        fileAdded: false
    },
    mongodb: {
        mongodbAvailable: false,
        connectionName: '',
        connectionStatus: '',
        connectionMongoUri: '',
        connectionMongoDb: null,
        loading: false
    },
    ameya_extract: {
        ameyaExtractAvailable: false,
        connectionName: '',
        connectionStatus: '',
        connectionMongoUri: '',
        connectionMongoDb: null,
        loading: false
    },
    appflyte: {
        appflyteAvailable: false,
        loading: false,
        appflyte_schemas: [],
        selected_schemas: [],
        purpose: null
    }
};

const TAB_ICONS = {
    google_drive: IconSvg.gDriveIcon,
    one_drive: IconSvg.onedriveIcon,
    files: IconSvg.textFileIcon,
    aws_s3: IconSvg.awsIcon,
    links: IconSvg.wwwIcon,
    ameya_extract: IconSvg.ameyaIcon,
    mongodb: IconSvg.mongoIcon,
    gmail: IconSvg.mailIcon,
    outlook: IconSvg.mailIcon,
    appflyte: IconSvg.aiQmsIcon
};

function DatasetAdd() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const intl = useIntl();
    const { selectedWorkspace, selectedProject, handleAnalyticsSteps } = useAppContext();
    const stateRef = useRef({});

    const [tabValue, setTabValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [sourceTypes, setSourceTypes] = useState(location.state?.source_types || []);
    const exceAnalytics = location.state?.excel_analytics_status || false;
    const all_projects = useSelector(state => state.all_data.projects);
    const [datasetId, setDatasetId] = useState(null);
    const [datasetName, setDatasetName] = useState('');

    const isInitLoading = Object.values(INITIAL_STATES).some(state => typeof state === 'object' &&
        Object.values(state).some(innerState => innerState?.initLoading === true)
    );

    const [googledriveData, setGoogledriveData] = useState(INITIAL_STATES.googledrive);
    const [onedriveData, setOnedriveData] = useState(INITIAL_STATES.onedrive);
    const [linksData, setLinksData] = useState(INITIAL_STATES.links);
    const [gmailData, setGmailData] = useState(INITIAL_STATES.gmail);
    const [outlookData, setOutlookData] = useState(INITIAL_STATES.outlook);
    const [fileData, setFileData] = useState(INITIAL_STATES.files);
    const [mongodbData, setMongodbData] = useState(INITIAL_STATES.mongodb);
    const [ameyaExtractData, setAmeyaExtractData] = useState(INITIAL_STATES.ameya_extract);
    const [appflyteData, setAppflyteData] = useState(INITIAL_STATES.appflyte);

    stateRef.current = { googledriveData, onedriveData, linksData, gmailData, outlookData, fileData, sourceTypes };
    const datasourceNames = useMemo(() => [...new Set((sourceTypes || []).map(item => item?.payload?.source_type))], [sourceTypes]);

    const formatLabel = useCallback((label) => {
        if (typeof label !== 'string') return '';
        return label.split('_').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    }, []);

    const getIntervalSeconds = useCallback((interval) => {
        return INTERVAL_SECONDS[interval] || 0;
    }, []);

    const updateDatasource = async (data) => {
        try {
            const integration_id = data?.payload?.__auto_id__;
            const update_key = data?.update_key
            const reqObj = {
                "id": integration_id,
                "fields": [
                    { "path": "$.auth_status", "value": "expired" },
                    { "path": "$.updated_on", "value": moment().format('YYYY-MM-DD HH:mm:ss') }
                ]
            }
            const res = await AnalyticsDataSourcesApi.update(JSON.stringify(reqObj), integration_id, update_key);
            if (res.status === 200) {
                dispatch(setAnalyticsDataSourcesAdded(true))
            }
            return
        } catch (error) {
            console.log(error)
            return
        }
    }

    const fetchGoogleFolderDetails = useCallback(async (item) => {
        setGoogledriveData(prev => ({ ...prev, loading: true }));

        try {
            const refresh_token = item?.payload?.refresh_token;
            if (!refresh_token) throw new Error('No refresh token');

            const tokenRes = await AmeyaAuthApi.getTokens(refresh_token, 'google');
            const tokenData = tokenRes.data ?? null;
            if (!tokenData) {
                await updateDatasource(item)
                setGoogledriveData(prev => ({ ...prev, loading: false }));
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate("/sources/analytics-tool/integrations")
                return
            }

            const { access_token, refresh_token: new_refresh_token } = tokenData;
            const reqObj = { access_token };
            const driveRes = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'gdrive');

            const rootFolders = driveRes.data.folders.map(folder => ({
                id: folder.id,
                label: folder.name,
                depth: 0,
                hasChildren: folder.hasChildren,
                childrenLoaded: false
            }));

            setGoogledriveData(prev => ({
                ...prev,
                googledriveFolders: rootFolders,
                accessToken: access_token,
                refreshToken: new_refresh_token,
                loading: false
            }));

        } catch (err) {
            console.error(err);
            setGoogledriveData(prev => ({ ...prev, loading: false }));
        }
    }, []);

    const fetchGoogleSubFolders = async (parentFolder) => {
        setGoogledriveData(prev => ({ ...prev, loading: true }));
        try {
            const reqObj = {
                access_token: googledriveData.accessToken,
                parent_id: parentFolder.id
            };

            const response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'gdrive');

            const children = response.data.folders.map(folder => ({
                id: folder.id,
                label: folder.name,
                parentId: parentFolder.id,
                depth: (parentFolder.depth || 0) + 1,
                hasChildren: folder.hasChildren
            }));

            setGoogledriveData(prev => ({
                ...prev,
                googledriveFolders: [...prev.googledriveFolders, ...children]
            }));

            parentFolder.childrenLoaded = true;

        } catch (err) {
            console.error(err);
        } finally {
            setGoogledriveData(prev => ({ ...prev, loading: false }));
        }
    };

    const fetchOneDriveFolderDetails = useCallback(async (item) => {
        setOnedriveData(prev => ({ ...prev, loading: true }));

        try {
            const refresh_token = item?.payload?.refresh_token;
            if (!refresh_token) {
                throw new Error('No refresh token available');
            }

            const response = await AmeyaAuthApi.getTokens(refresh_token, 'azure');
            const tokenData = response.data ?? null;
            if (!tokenData) {
                await updateDatasource(item)
                setOnedriveData(prev => ({ ...prev, loading: false }));
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate("/sources/analytics-tool/integrations")
                return
            }

            if (response.status !== 200) {
                throw new Error(`Failed to get tokens: ${response.status}`);
            }

            const { access_token, refresh_token: new_refresh_token } = response.data || {};
            if (!access_token || !new_refresh_token) {
                throw new Error('Invalid tokens received');
            }

            const reqObj = { access_token };
            const drive_response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'onedrive');

            if (drive_response.status !== 200) {
                throw new Error(`Failed to fetch folders: ${drive_response.status}`);
            }

            const all_folders = drive_response.data?.folders || [];
            if (all_folders.length === 0) {
                console.warn('No folders found in OneDrive');
            }

            const formattedFolders = all_folders.map(folder => ({
                ...folder,
                label: folder.label || folder.name || folder.id,
                id: folder.id
            }));

            setOnedriveData(prev => ({
                ...prev,
                onedriveFolders: formattedFolders,
                accessToken: access_token,
                refreshToken: new_refresh_token,
                loading: false
            }));

        } catch (error) {
            console.error('OneDrive folder fetch error:', error);
            tostAlert(intl.formatMessage(messages.onedrive_folder_error), 'error');
            setOnedriveData(prev => ({
                ...prev,
                loading: false,
                formErrors: { ...prev.formErrors, folderSelected: 'Failed to load folders' }
            }));
        }
    }, []);

    const fetchOnedriveSubFolders = async (parentFolder) => {
        setOnedriveData(prev => ({ ...prev, loading: true }));
        try {

            const reqObj = { access_token: onedriveData.accessToken, parent_id: parentFolder.id };
            const response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'onedrive');

            if (response.status !== 200) return;

            const subfolders = response.data.folders.map(folder => ({
                ...folder,
                label: folder.name || folder.id,
                depth: (parentFolder.depth || 0) + 1,
                hasChildren: folder.hasChildren || false
            }));

            setOnedriveData(prev => {
                const index = prev.onedriveFolders.findIndex(f => f.id === parentFolder.id);
                if (index === -1) return prev;

                const newFolders = [
                    ...prev.onedriveFolders.slice(0, index + 1),
                    ...subfolders,
                    ...prev.onedriveFolders.slice(index + 1)
                ];

                return {
                    ...prev,
                    onedriveFolders: newFolders
                };
            });
        } catch (error) {
            console.log(error)
        } finally {
            setOnedriveData(prev => ({ ...prev, loading: false }));
        }
    };

    const fetchAuthDetails = useCallback(async (item, provider, setState) => {
        setState(prev => ({ ...prev, loading: true }));

        try {
            const refresh_token = item?.payload?.refresh_token;
            if (!refresh_token) {
                throw new Error('No refresh token available');
            }

            const response = await AmeyaAuthApi.getTokens(refresh_token, provider);
            const tokenData = response.data ?? null;
            if (!tokenData) {
                await updateDatasource(item)
                setState(prev => ({ ...prev, loading: false }));
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate("/sources/analytics-tool/integrations")
                return
            }

            if (response.status !== 200) {
                throw new Error(`Failed to get tokens: ${response.status}`);
            }

            const { access_token, refresh_token: new_refresh_token } = response.data || {};
            if (!access_token || !new_refresh_token) {
                throw new Error('Invalid tokens received');
            }

            setState(prev => ({
                ...prev,
                accessToken: access_token,
                refreshToken: new_refresh_token,
                loading: false
            }));

        } catch (error) {
            console.error(`${provider} auth error:`, error);
            tostAlert(`Failed to load ${provider} token. Please try again.`, 'error');
            setState(prev => ({ ...prev, loading: false }));
        }
    }, []);

    const getAllAnalyticsSourceFiles = async () => {
        setFileData(prev => ({ ...prev, loading: true }));
        try {

            const response = await AnalyticsFilesData(datasetId);
            setFileData(prev => ({ ...prev, fileAdded: false }));

            if (exceAnalytics) {
                const filtered_response = response?.filter(item => {
                    const url = item?.payload?.file_url?.toLowerCase();
                    const cleanUrl = url?.split('?')[0];
                    return cleanUrl?.endsWith('.xlsx');
                });

                setFileData(prev => ({ ...prev, fileList: filtered_response }));
                return filtered_response;

            } else {
                const filtered_response = response?.filter(item => {
                    const url = item?.payload?.file_url?.toLowerCase();
                    const cleanUrl = url?.split('?')[0];
                    return !cleanUrl?.endsWith('.xlsx');
                });

                setFileData(prev => ({ ...prev, fileList: filtered_response }));
                return filtered_response;
            }

        } catch (error) {
            console.error('Error fetching analytics files:', error);
            tostAlert(intl.formatMessage(messages.file_load_error), 'error');
            return [];
        } finally {
            setFileData(prev => ({ ...prev, loading: false }));
        }
    }

    const validateDataSources = () => {
        const currentState = stateRef.current;
        const errors = [];

        if (datasourceNames.includes('files') && !currentState.fileData.fileList?.length) {
            setTabValue('files');
            errors.push(intl.formatMessage(messages.file_select_error));
        }

        if (datasourceNames.includes('links') && !currentState.linksData.links?.length) {
            setTabValue('links');
            errors.push(intl.formatMessage(messages.url_add_error));
        }

        if (datasourceNames.includes('google_drive') && !currentState.googledriveData.googledriveSelectedFolders) {
            setTabValue('google_drive');
            errors.push(intl.formatMessage(messages.gdrive_add_error));
        }

        if (datasourceNames.includes('one_drive') && !currentState.onedriveData.onedriveSelectedFolders) {
            setTabValue('one_drive');
            errors.push(intl.formatMessage(messages.onedrive_add_error));
        }

        if (datasourceNames.includes('gmail') && !currentState.gmailData.subjectMessage?.trim()) {
            setTabValue('gmail');
            errors.push(intl.formatMessage(messages.gmail_subject_message_error));
        }

        if (datasourceNames.includes('outlook') && !currentState.outlookData.subjectMessage?.trim()) {
            setTabValue('outlook');
            errors.push(intl.formatMessage(messages.outlook_subject_message_error));
        }

        if (datasourceNames.includes('appflyte') && appflyteData.selected_schemas?.length === 0) {
            setTabValue('appflyte');
            errors.push(intl.formatMessage(messages.appflyte_project_message_error));
        }

        if (errors.length > 0) {
            tostAlert(errors[0], 'warning');
            return false;
        }

        return true;
    }

    const createDataSourceHandlers = useCallback(() => ({
        google_drive: async (sourceTypes, settingsReqObj, connectionObj, authConfig) => {
            const currentState = stateRef.current;
            const google_drive_data = sourceTypes.find(item => item?.payload?.source_type === "google_drive");
            if (!google_drive_data) return false;

            const { accessToken, refreshToken, googledriveSelectedFolders, importInterval } = currentState.googledriveData;
            if (!accessToken || !refreshToken) {
                tostAlert(intl.formatMessage(messages.gdrive_token_error), 'warning');
                return false;
            }

            const interval_seconds = getIntervalSeconds(importInterval);
            const folder_id = googledriveSelectedFolders?.id || googledriveSelectedFolders;
            const job_id = uuid();

            connectionObj.google_drive = [{
                job_id: job_id,
                client_id: authConfig.google_auth.client_id,
                folder_id,
                connection_id: google_drive_data?.payload.__auto_id__,
                access_token: accessToken,
                client_secret: authConfig.google_auth.client_secret,
                refresh_token: refreshToken,
                interval_seconds
            }];

            settingsReqObj.source_types.push('google_drive');
            settingsReqObj.source_details.push({
                source_type: 'google_drive',
                interval_seconds,
                source_id: google_drive_data?.payload.__auto_id__,
                job_id: job_id,
                folder_id,
                refresh_token: refreshToken,
            })

            return true;
        },

        one_drive: async (sourceTypes, settingsReqObj, connectionObj, authConfig) => {
            const currentState = stateRef.current;
            const one_drive_data = sourceTypes.find(item => item?.payload?.source_type === "one_drive");
            if (!one_drive_data) return false;

            const { accessToken, refreshToken, onedriveSelectedFolders, importInterval } = currentState.onedriveData;
            if (!accessToken || !refreshToken) {
                tostAlert(intl.formatMessage(messages.onedrive_token_error), 'warning');
                return false;
            }

            const interval_seconds = getIntervalSeconds(importInterval);
            const folder_id = onedriveSelectedFolders?.id || onedriveSelectedFolders;
            const job_id = uuid();

            connectionObj.one_drive = [{
                job_id: job_id,
                user_id: one_drive_data?.payload?.email,
                folder_id,
                client_id: authConfig.azure_auth.client_id,
                tenant_id: authConfig.azure_auth.tenant_id,
                connection_id: one_drive_data?.payload?.__auto_id__,
                access_token: accessToken,
                client_secret: authConfig.azure_auth.client_secret,
                refresh_token: refreshToken,
                interval_seconds
            }];

            settingsReqObj.source_types.push('one_drive');
            settingsReqObj.source_details.push({
                source_type: 'one_drive',
                interval_seconds,
                source_id: one_drive_data?.payload?.__auto_id__,
                job_id: job_id,
                folder_id,
                user_id: one_drive_data.email,
                refresh_token: refreshToken
            });

            return true;
        },

        gmail: async (sourceTypes, settingsReqObj, connectionObj, authConfig) => {
            const currentState = stateRef.current;
            const gmail_data = sourceTypes.find(item => item?.payload?.source_type === "gmail");
            if (!gmail_data) return false;

            const { accessToken, refreshToken, subjectMessage, importInterval } = currentState.gmailData;
            if (!accessToken || !refreshToken) {
                tostAlert(intl.formatMessage(messages.gmail_token_error), 'warning');
                return false;
            }

            const interval_seconds = getIntervalSeconds(importInterval);
            const job_id = uuid();

            connectionObj.google_email = {
                job_id: job_id,
                client_id: authConfig.google_auth.client_id,
                connection_id: gmail_data?.payload?.__auto_id__,
                access_token: accessToken,
                client_secret: authConfig.google_auth.client_secret,
                refresh_token: refreshToken,
                subject_message: subjectMessage,
                target_emails: [gmail_data?.payload?.email],
                interval_seconds
            };

            settingsReqObj.source_types.push('gmail');
            settingsReqObj.source_details.push({
                source_type: 'gmail',
                interval_seconds,
                source_id: gmail_data?.payload?.__auto_id__,
                job_id: job_id,
                target_emails: [gmail_data?.payload?.email],
                subject_message: subjectMessage,
                refresh_token: refreshToken,
            });

            return true;
        },

        outlook: async (sourceTypes, settingsReqObj, connectionObj, authConfig) => {
            const currentState = stateRef.current;
            const outlook_data = sourceTypes.find(item => item?.payload?.source_type === "outlook");
            if (!outlook_data) return false;

            const { accessToken, refreshToken, subjectMessage, importInterval } = currentState.outlookData;
            if (!accessToken || !refreshToken) {
                tostAlert(intl.formatMessage(messages.outlook_token_error), 'warning');
                return false;
            }

            const interval_seconds = getIntervalSeconds(importInterval);
            const job_id = uuid();

            connectionObj.outlook_email = {
                job_id: job_id,
                tenant_id: authConfig.azure_auth.tenant_id,
                client_id: authConfig.azure_auth.client_id,
                access_token: accessToken,
                connection_id: outlook_data?.payload?.__auto_id__,
                refresh_token: refreshToken,
                client_secret: authConfig.azure_auth.client_secret,
                subject_message: subjectMessage,
                target_emails: [outlook_data?.payload?.email],
                interval_seconds
            };

            settingsReqObj.source_types.push('outlook');
            settingsReqObj.source_details.push({
                source_type: 'outlook',
                interval_seconds,
                source_id: outlook_data?.payload?.__auto_id__,
                job_id: job_id,
                target_emails: [outlook_data?.payload?.email],
                subject_message: subjectMessage,
                refresh_token: refreshToken
            });

            return true;
        },

        links: async (sourceTypes, settingsReqObj, connectionObj) => {
            const currentState = stateRef.current;
            const links_data = sourceTypes.find(item => item?.payload?.source_type === "links");
            if (!links_data) return false;

            const { links, importInterval } = currentState.linksData;

            const updated_links = (links || []).filter(item => item?.url?.trim()).map(item => ({ url: item.url.trim() }));
            const interval_seconds = getIntervalSeconds(importInterval);
            const job_id = uuid();

            if (updated_links.length === 0) {
                tostAlert(intl.formatMessage(messages.links_error), 'warning');
                return false;
            }

            connectionObj.links = {
                job_id: job_id,
                connection_id: links_data?.payload?.__auto_id__,
                urls: updated_links,
                interval_seconds: interval_seconds
            };

            settingsReqObj.source_types.push('links');
            settingsReqObj.source_details.push({
                source_type: 'links',
                interval_seconds: importInterval,
                job_id: job_id,
                source_id: links_data?.payload?.__auto_id__,
                all_links: links
            });

            return true;
        },

        files: async (sourceTypes, settingsReqObj, connectionObj) => {
            const currentState = stateRef.current;
            const files_data = sourceTypes.find(item => item?.payload?.source_type === "files");
            if (!files_data) return false;

            const selectedFiles = currentState.fileData.fileList || [];
            const job_id = uuid();
            const updated_file_data = selectedFiles.filter(item => item?.payload?.file_url).map(item => item?.payload?.file_url?.split("?")[0]);
            const updated_file_details = selectedFiles.filter(item => item?.payload?.file_url).map(item => ({
                "file_id": item.payload.__auto_id__,
                "file_name": item.payload.file_name,
                "status": "Processing"
            }));

            if (updated_file_data.length === 0) {
                tostAlert(intl.formatMessage(messages.file_selection_error), 'warning');
                return false;
            }

            connectionObj.direct_files = {
                job_id: job_id,
                bucket_name: window.__ENV__.S3_BUCKET_NAME,
                files: updated_file_data,
                connection_id: files_data?.payload?.__auto_id__,
            };

            settingsReqObj.source_types.push('files');
            settingsReqObj.source_details.push({
                job_id: job_id,
                source_type: 'files',
                source_id: files_data?.payload?.__auto_id__,
                bucket_name: window.__ENV__.S3_BUCKET_NAME,
                files: updated_file_details
            });

            return true;
        }

    }), [getIntervalSeconds]);

    const getCloudAuthConfig = useCallback(async (datasourceNames) => {
        const needsCloudAuth = datasourceNames.some(name =>
            ['google_drive', 'one_drive', 'gmail', 'outlook'].includes(name)
        );

        if (!needsCloudAuth) {
            return { google_auth: {}, azure_auth: {} };
        }

        try {
            const subscription_id = await fetchSubscriptionId();
            if (!subscription_id) {
                throw new Error('Subscription ID not found');
            }

            const subscriptions = await getSubscriptionById(subscription_id);
            const subscriptionData = subscriptions?.payload;
            if (!subscriptionData) {
                throw new Error('Subscription data not found');
            }

            const authConfig = subscriptionData.ameya_auth_config || {};
            const google_auth = authConfig.google || {};
            const azure_auth = authConfig.azure || {};

            const needsGoogle = datasourceNames.some(name =>
                ['google_drive', 'gmail'].includes(name)
            );
            const needsAzure = datasourceNames.some(name =>
                ['one_drive', 'outlook'].includes(name)
            );

            if (needsGoogle && (!google_auth.client_id || !google_auth.client_secret)) {
                throw new Error('Google authentication configuration is incomplete');
            }

            if (needsAzure && (!azure_auth.client_id || !azure_auth.client_secret || !azure_auth.tenant_id)) {
                throw new Error('Azure authentication configuration is incomplete');
            }

            return { google_auth, azure_auth };

        } catch (error) {
            console.error('Authentication configuration error:', error);
            tostAlert(`Authentication error: ${error.message}`, 'warning');
            return null;
        }
    }, []);

    const handleMongoDBDataset = async () => {

        const source_ids = (sourceTypes || [])
            .filter(item => item?.payload?.source_type === 'mongodb')
            .map(item => item?.payload?.__auto_id__).filter(Boolean) ?? [];

        if (source_ids.length === 0) {
            tostAlert(intl.formatMessage(messages.mongo_source_error), 'warning');
            return;
        }

        const reqObj = {
            dataset_name: datasetName,
            source_id: source_ids,
            dataset_id: `mongo_${datasetId}`,
            excel_analytics_status: false
        };

        const datasetResponse = await AnalyticsApi.createDataset(JSON.stringify(reqObj));
        if (datasetResponse.status !== 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_error), 'error');
            return;
        }

        const descriptionReqObj = {
            "dataset_id": `mongo_${datasetId}`,
        }

        const description_response = await AnalyticsApi.createDatasetDescription(descriptionReqObj)
        if (description_response.status !== 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_error), 'error');
            return;
        }

        const userName = getUserName();
        const workspace_id = selectedWorkspace?.payload?.__auto_id__;
        const project_id = selectedProject?.payload?.__auto_id__;

        const dataObject = {
            collection_item: {
                dataset_id: `mongo_${datasetId}`,
                dataset_name: datasetName,
                excel_analytics_status: false,
                source_types: ["mongodb"],
                source_details: [{
                    source_type: 'mongodb',
                    source_ids: source_ids
                }],
                status: 'Success',
                workspace_id,
                project_id,
                created_by: userName,
                created_on: moment().format('YYYY-MM-DD HH:mm:ss'),
                __auto_id__: datasetId
            }
        };

        const addResponse = await AnalyticsDataSetsApi.addData(JSON.stringify(dataObject));
        if (addResponse.status === 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_success), "success");
            localStorage.removeItem("dataset_id")
            dispatch(setAnalyticsDataSetAdded(true))
            handleAnalyticsSteps('analytics_dataset_creation_status')
            navigate('/data-sets');
        } else {
            throw new Error('Failed to add dataset');
        }

    }

    const handleAmeyaExtractDataset = async () => {

        const ameya_extract_sources = (sourceTypes || []).filter(item => item?.payload?.source_type === 'ameya_extract')
        const source_ids = (ameya_extract_sources || []).map(item => item?.payload?.__auto_id__).filter(Boolean) ?? [];
        const extraction_projects = (ameya_extract_sources || []).flatMap(item => item?.payload?.extraction_projects).filter(Boolean) ?? [];

        if (source_ids.length === 0) {
            tostAlert(intl.formatMessage(messages.ameya_extract_error), 'warning');
            return;
        }

        const reqObj = {
            dataset_name: datasetName,
            source_id: source_ids,
            dataset_id: `mongo_${datasetId}`,
            excel_analytics_status: false
        };

        const datasetResponse = await AnalyticsApi.createDataset(JSON.stringify(reqObj));
        if (datasetResponse.status !== 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_error), 'error');
            return;
        }

        const descriptionReqObj = {
            "dataset_id": `mongo_${datasetId}`,
        }

        const description_response = await AnalyticsApi.createDatasetDescription(descriptionReqObj)
        if (description_response.status !== 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_error), 'error');
            return;
        }

        const userName = getUserName();
        const workspace_id = selectedWorkspace?.payload?.__auto_id__;
        const project_id = selectedProject?.payload?.__auto_id__;

        const dataObject = {
            collection_item: {
                dataset_id: `mongo_${datasetId}`,
                dataset_name: datasetName,
                excel_analytics_status: false,
                source_types: ["ameya_extract"],
                source_details: [{
                    source_type: 'ameya_extract',
                    source_ids: source_ids
                }],
                extraction_projects: extraction_projects,
                status: 'Success',
                workspace_id,
                project_id,
                created_by: userName,
                created_on: moment().format('YYYY-MM-DD HH:mm:ss'),
                __auto_id__: datasetId
            }
        };

        const addResponse = await AnalyticsDataSetsApi.addData(JSON.stringify(dataObject));
        if (addResponse.status === 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_success), "success");
            localStorage.removeItem("dataset_id")
            dispatch(setAnalyticsDataSetAdded(true))
            navigate('/data-sets');
        } else {
            throw new Error('Failed to add dataset');
        }

    }

    const handleAppflyteDataset = async () => {

        const schema_ids = (appflyteData.selected_schemas || []).map(item => item?.payload?.__auto_id__) ?? [];

        const reqObj = {
            dataset_name: datasetName,
            source_id: schema_ids,
            dataset_id: `${appflyteData.purpose}#${datasetId}`,
        };

        const datasetResponse = await AnalyticsApi.createDataset(JSON.stringify(reqObj));
        if (datasetResponse.status !== 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_error), 'error');
            return;
        }

        const descriptionReqObj = {
            "dataset_id": `${appflyteData.purpose}#${datasetId}`,
        }

        const description_response = await AnalyticsApi.createDatasetDescription(descriptionReqObj)
        if (description_response.status !== 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_error), 'error');
            return;
        }

        const userName = getUserName();
        const workspace_id = selectedWorkspace?.payload?.__auto_id__;
        const project_id = selectedProject?.payload?.__auto_id__;

        const dataObject = {
            collection_item: {
                dataset_id: `${appflyteData.purpose}#${datasetId}`,
                dataset_name: datasetName,
                excel_analytics_status: false,
                source_types: ["appflyte"],
                source_details: [{
                    source_type: 'appflyte',
                    schema_ids: schema_ids
                }],
                status: 'Success',
                workspace_id,
                project_id,
                created_by: userName,
                created_on: moment().format('YYYY-MM-DD HH:mm:ss'),
                __auto_id__: datasetId
            }
        };

        const addResponse = await AnalyticsDataSetsApi.addData(JSON.stringify(dataObject));
        if (addResponse.status === 200) {
            tostAlert(intl.formatMessage(messages.dataset_add_success), "success");
            localStorage.removeItem("dataset_id")
            dispatch(setAnalyticsDataSetAdded(true))
            navigate('/data-sets');
        } else {
            throw new Error('Failed to add dataset');
        }

    }

    const handleFileBasedDataset = async (datasourceNames) => {
        try {

            const userName = getUserName();
            const alertEmail = await fetchUserEmail();
            const workspace_id = selectedWorkspace?.payload?.__auto_id__;
            const project_id = selectedProject?.payload?.__auto_id__;
            const prefix = exceAnalytics ? "excel" : "files";
            const dataset_id = `${prefix}_${datasetId}`;

            const connectionObj = {};
            const settingsReqObj = {
                dataset_id: dataset_id,
                dataset_name: datasetName,
                excel_analytics_status: exceAnalytics,
                status: 'Processing',
                alert_email: alertEmail,
                source_types: [],
                source_details: [],
                workspace_id,
                project_id,
                created_by: userName,
                created_on: moment().format('YYYY-MM-DD HH:mm:ss'),
                __auto_id__: datasetId
            };

            const authConfig = await getCloudAuthConfig(datasourceNames);
            if (!authConfig) return;

            const DATA_SOURCE_HANDLERS = createDataSourceHandlers();

            for (const sourceName of datasourceNames) {
                const handler = DATA_SOURCE_HANDLERS[sourceName];
                if (handler) {
                    const result = await handler(sourceTypes, settingsReqObj, connectionObj, authConfig);
                    if (!result) return;
                } else {
                    console.warn(`Unknown data source: ${sourceName}`);
                }
            }

            const agentTokenId = await getAgentApiToken()

            const datasetObj = {
                dataset_name: datasetName,
                source_id: [dataset_id],
                dataset_id: dataset_id
            };

            const datasetResponse = await AnalyticsApi.createDataset(JSON.stringify(datasetObj));
            if (datasetResponse.status !== 200) {
                tostAlert(intl.formatMessage(messages.dataset_add_error), 'error');
                return;
            }

            const analyticsObj = {
                "auth_token": agentTokenId,
                "dataset_id": dataset_id,
                "dataset_name": datasetName,
                "alert_email": alertEmail,
                "job_type": "an",
                "is_excel": false,
                "connections": connectionObj
            }

            const response = await MonitoringServiceApi.createJob(JSON.stringify(analyticsObj), project_id)
            if (response.status !== 200 && response.status !== 201) {
                tostAlert(intl.formatMessage(messages.dataset_add_error), "error");
                return
            }

            const dataObject = {
                collection_item: {
                    ...settingsReqObj,
                }
            };

            const addResponse = await AnalyticsDataSetsApi.addData(JSON.stringify(dataObject));
            if (addResponse.status === 200) {
                tostAlert(intl.formatMessage(messages.dataset_add_success), "success");
                localStorage.removeItem("dataset_id")
                dispatch(setAnalyticsDataSetAdded(true))
                navigate('/data-sets');
            } else {
                throw new Error('Failed to add dataset');
            }

        } catch (error) {
            console.error('File-based dataset error:', error);
            throw error;
        }
    }

    const handleSave = async () => {
        setLoading(true)
        try {

            if (!datasetName?.trim()) {
                tostAlert(intl.formatMessage(messages.dataset_name_error), 'warning');
                return;
            }

            if (!validateDataSources()) {
                return;
            }

            await handleDatasetCreation();

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleDatasetCreation = async () => {
        try {
            if (datasourceNames.includes('mongodb')) {
                return await handleMongoDBDataset();
            }
            if (datasourceNames.includes('ameya_extract')) {
                return await handleAmeyaExtractDataset();
            }
            if (datasourceNames.includes('appflyte')) {
                return await handleAppflyteDataset();
            }
            return await handleFileBasedDataset(datasourceNames);
        } catch (error) {
            console.error('Dataset creation error:', error);
            tostAlert(intl.formatMessage(messages.dataset_add_error), 'error');
        }
    }

    const handleConnectionDeselect = useCallback((tab) => {
        if (sourceTypes.length < 2) return;

        const updatedSourceTypes = sourceTypes.filter(item => item?.payload?.__auto_id__ !== tab?.payload?.__auto_id__);
        setSourceTypes(updatedSourceTypes);

        if (tabValue === tab.connector && updatedSourceTypes.length > 0) {
            setTabValue(updatedSourceTypes[0].connector);
        }
    }, [sourceTypes, tabValue]);

    useEffect(() => {

        const dataset_item_id = JSON.parse(localStorage.getItem('dataset_id')) ?? null;
        const dataset_name = location.state?.dataset_name ?? null;

        if (!sourceTypes?.length || !dataset_item_id || !dataset_name) {
            navigate('/data-sets/datasource');
            return;
        }

        setDatasetId(dataset_item_id)
        const defaultTab = sourceTypes[0];
        setTabValue(defaultTab.payload?.source_type);
        setDatasetName(dataset_name)

        sourceTypes.forEach(source => {
            const connector = source?.payload?.source_type;
            switch (connector) {
                case 'one_drive':
                    setOnedriveData(prev => ({ ...prev, onedriveAvailable: true }));
                    break;
                case 'google_drive':
                    setGoogledriveData(prev => ({ ...prev, googledriveAvailable: true }));
                    break;
                case 'gmail':
                    setGmailData(prev => ({ ...prev, gmailAvailable: true }));
                    break;
                case 'outlook':
                    setOutlookData(prev => ({ ...prev, outlookAvailable: true }));
                    break;
                case 'files':
                    setFileData(prev => ({ ...prev, fileAvailable: true }));
                    break;
                case 'mongodb':
                    setMongodbData(prev => ({ ...prev, mongodbAvailable: true }));
                    break;
                case 'ameya_extract':
                    setAmeyaExtractData(prev => ({ ...prev, ameyaExtractAvailable: true }));
                    break;
                case 'appflyte':
                    setAppflyteData(prev => ({ ...prev, appflyteAvailable: true }));
                    break;
            }
        });
    }, [sourceTypes, navigate]);

    useEffect(() => {
        if (googledriveData.googledriveAvailable) {
            const gdriveConnector = sourceTypes.find(item => item?.payload?.source_type === "google_drive");
            if (gdriveConnector) {
                setGoogledriveData(prev => ({ ...prev, googledriveConnector: gdriveConnector }));
                fetchGoogleFolderDetails(gdriveConnector);
            }
        }
    }, [googledriveData.googledriveAvailable, sourceTypes, fetchGoogleFolderDetails]);

    useEffect(() => {
        if (onedriveData.onedriveAvailable) {
            const onedriveConnector = sourceTypes.find(item => item?.payload?.source_type === "one_drive");
            if (onedriveConnector) {
                setOnedriveData(prev => ({ ...prev, onedriveConnector: onedriveConnector }));
                fetchOneDriveFolderDetails(onedriveConnector);
            }
        }
    }, [onedriveData.onedriveAvailable, sourceTypes, fetchOneDriveFolderDetails]);

    useEffect(() => {
        if (gmailData.gmailAvailable) {
            const gmailConnector = sourceTypes.find(item => item?.payload?.source_type === "gmail");
            if (gmailConnector) {
                setGmailData(prev => ({ ...prev, gmailConnector: gmailConnector }));
                fetchAuthDetails(gmailConnector, 'google', setGmailData);
            }
        }
    }, [gmailData.gmailAvailable, sourceTypes, fetchAuthDetails]);

    useEffect(() => {
        if (outlookData.outlookAvailable) {
            const outlookConnector = sourceTypes.find(item => item?.payload?.source_type === "outlook");
            if (outlookConnector) {
                setOutlookData(prev => ({ ...prev, outlookConnector: outlookConnector }));
                fetchAuthDetails(outlookConnector, 'azure', setOutlookData);
            }
        }
    }, [outlookData.outlookAvailable, sourceTypes, fetchAuthDetails]);

    useEffect(() => {
        const fetchFiles = async () => {
            if (!fileData.fileAvailable) return;

            const fileConnector = sourceTypes.find(item => item?.payload?.source_type === "files");
            if (!fileConnector) return;

            try {
                getAllAnalyticsSourceFiles();
            } catch (error) {
                console.error('Error fetching files:', error);
            }
        };
        fetchFiles();
    }, [fileData.fileAvailable, fileData.fileAdded]);

    useEffect(() => {
        if (mongodbData.mongodbAvailable) {
            const filtered_datasource = (sourceTypes || [])?.find(item => item?.payload?.source_type === "mongodb")
            const connection_name = filtered_datasource?.payload?.name ?? '';
            const connection_mongo_uri = filtered_datasource?.payload?.connection_url ?? '';
            setMongodbData(prev => ({
                ...prev, connectionName: connection_name, connectionMongoUri: connection_mongo_uri
            }))
        }
    }, [mongodbData.mongodbAvailable])

    useEffect(() => {
        if (ameyaExtractData.ameyaExtractAvailable) {
            const filtered_datasource = (sourceTypes || [])?.find(item => item?.payload?.source_type === "ameya_extract")
            const connection_name = filtered_datasource?.payload?.name ?? '';
            const connection_mongo_uri = filtered_datasource?.payload?.connection_url ?? '';
            setAmeyaExtractData(prev => ({
                ...prev, connectionName: connection_name, connectionMongoUri: connection_mongo_uri
            }))
        }
    }, [ameyaExtractData.ameyaExtractAvailable])

    useEffect(() => {
        if (appflyteData.appflyteAvailable) {
            setAppflyteData(prev => ({ ...prev, loading: true }))
            const filtered_datasource = (sourceTypes || [])?.find(item => item?.payload?.source_type === "appflyte")
            const dataset_purpose = filtered_datasource?.payload?.purpose ?? 'appflyte_query';
            const appflyte_schemas = filtered_datasource?.payload?.appflyte_schemas ?? [];
            const matchingProjects = all_projects.filter(project => appflyte_schemas.includes(project.payload?.__auto_id__)) ?? [];
            setAppflyteData(prev => ({
                ...prev,
                appflyte_schemas: matchingProjects,
                loading: false,
                selected_schemas: matchingProjects,
                purpose: dataset_purpose
            }))
        }
    }, [appflyteData.appflyteAvailable])

    const breadcrumbs = useMemo(() => [
        <Link
            underline="hover"
            key="1"
            color="inherit"
            onClick={() => navigate('/data-sets/datasource')}
            sx={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}
        >
            <IconButton>
                <ArrowBack sx={{ color: '#0B51C5', fontSize: '24px' }} />
            </IconButton>
            <Typography sx={styles.linkText}>All Datasets</Typography>
        </Link>,
        <Typography
            key="2"
            sx={{
                ...styles.linkText,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '200px'
            }}
        >
            {formatLabel(datasetName)}
        </Typography>
    ], [navigate, styles.linkText]);

    if (!sourceTypes?.length) {
        navigate('/data-sets/datasource')
    }

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>
                {(loading || isInitLoading) && <LoadBar />}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack spacing={2} sx={{ marginLeft: '0' }}>
                        <Breadcrumbs
                            separator={<NavigateNextRounded fontSize="small" />}
                            aria-label="breadcrumb"
                        >
                            {breadcrumbs}
                        </Breadcrumbs>
                    </Stack>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
                            onClick={handleSave}
                            disabled={loading || isInitLoading}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                SAVE
                            </Typography>
                        </Button>
                        <Button
                            onClick={() => navigate('/data-sets/datasource')}
                            sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#404040' }}>
                                CANCEL
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: 'inline-block', borderBottom: 1, borderColor: 'divider', marginTop: '15px' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(event, newValue) => setTabValue(newValue)}
                        aria-label="dynamic tabs"
                    >
                        {sourceTypes.map((tab) => (
                            <Tab
                                key={tab?.payload?.source_type}
                                value={tab?.payload?.source_type}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <ReactSVG
                                            src={TAB_ICONS[tab?.payload?.source_type]}
                                            beforeInjection={(svg) => {
                                                const isActive = tabValue === tab?.payload?.source_type;
                                                svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                                svg.setAttribute('stroke', isActive ? '#1976d2' : '#000000');
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '16px', fontWeight: 600, textTransform: 'none' }}>
                                            {formatLabel(tab?.payload?.source_type)}
                                        </Typography>

                                        {sourceTypes.length > 1 && (
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleConnectionDeselect(tab);
                                                }}
                                                size="small"
                                            >
                                                <Cancel sx={{
                                                    color: tabValue === tab?.payload?.source_type ? '#1976d2' : 'inherit',
                                                    fontSize: '18px'
                                                }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                <Box sx={{ marginTop: '20px' }}>

                    {tabValue === "files" && (
                        <Files
                            fileData={fileData}
                            setFileData={setFileData}
                            exceAnalytics={exceAnalytics}
                            onReload={getAllAnalyticsSourceFiles}
                        />
                    )}

                    {tabValue === "mongodb" && (
                        <MongoDB
                            setMongodbData={setMongodbData}
                            mongodbData={mongodbData}
                        />
                    )}

                    {tabValue === "google_drive" && (
                        <GoogleDrive
                            loading={loading}
                            googledriveData={googledriveData}
                            setGoogledriveData={setGoogledriveData}
                            fetchGoogleSubFolders={fetchGoogleSubFolders}
                            onReload={fetchGoogleFolderDetails}
                        />
                    )}

                    {tabValue === "one_drive" && (
                        <OneDrive
                            loading={loading}
                            onedriveData={onedriveData}
                            setOnedriveData={setOnedriveData}
                            onReload={fetchOneDriveFolderDetails}
                            fetchSubFolders={fetchOnedriveSubFolders}
                        />
                    )}

                    {tabValue === "gmail" && (
                        <Gmail
                            loading={loading}
                            gmailData={gmailData}
                            setGmailData={setGmailData}
                        />
                    )}

                    {tabValue === "outlook" && (
                        <Outlook
                            loading={loading}
                            outlookData={outlookData}
                            setOutlookData={setOutlookData}
                        />
                    )}

                    {tabValue === "links" && (
                        <Links
                            loading={loading}
                            linksData={linksData}
                            setLinksData={setLinksData}
                        />
                    )}

                    {/* {tabValue === "aws_s3" && (
                        <AWS_S3
                            source_types={sourceTypes}
                            loading={loading}
                            setDataLoading={setDataLoading}
                        />
                    )} */}

                    {tabValue === "ameya_extract" && (
                        <AmeyaExtract
                            setAmeyaExtractData={setAmeyaExtractData}
                            ameyaExtractData={ameyaExtractData}
                        />
                    )}

                    {tabValue === "appflyte" && (
                        <Appflyte
                            setAppflyteData={setAppflyteData}
                            appflyteData={appflyteData}
                        />
                    )}

                </Box>
            </Box>
        </Box>
    );
}

export default DatasetAdd;